import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import {
  isRetriableFailure,
  isUnauthorized,
  markSessionExpired,
  SESSION_QUERY_KEY,
  shouldRevalidateSession,
} from '@/lib/session-expiry';

/**
 * Builds the app's query client, wired for session expiry.
 *
 * A factory rather than a bare `new QueryClient(...)` so tests can exercise the
 * wiring on an isolated instance. The pure decisions live in `lib/session-expiry.ts`
 * and are tested there; what needs proving HERE is that they are actually connected —
 * the decisions were correct and unreachable before this existed.
 */
export function createAppQueryClient(): QueryClient {
  // A holder, not a `let`: the caches must be constructed BEFORE the client that
  // owns them, yet their callbacks need the client to invalidate with. The callbacks
  // cannot run until construction completes, so the late binding is safe — and a
  // const holder keeps that explicit rather than relying on assignment timing.
  const app: { client?: QueryClient } = {};

  /**
   * Revalidate the session so AuthGate re-renders and shows the sign-in screen.
   *
   * There is deliberately no redirect. AuthGate is a layout route wrapping every
   * operator surface, so re-running the session probe is enough and is better: the
   * operator keeps the URL they were on and returns to it after signing in. A
   * redirect would also be wrong on `/`, which is public — a lapsed session there
   * should reveal the product page, not bounce the visitor somewhere.
   */
  const revalidateSession = (): void => {
    markSessionExpired();
    void app.client?.invalidateQueries({ queryKey: [SESSION_QUERY_KEY] });
  };

  const queryCache = new QueryCache({
    onError: (error, query): void => {
      if (shouldRevalidateSession({ error, queryKey: query.queryKey })) revalidateSession();
    },
  });

  const mutationCache = new MutationCache({
    onError: (error): void => {
      // Mutations carry no key to exclude, but the session probe is a query and is
      // never mutated, so a 401 from any mutation is unambiguous.
      if (isUnauthorized(error)) revalidateSession();
    },
  });

  app.client = new QueryClient({
    queryCache,
    mutationCache,
    defaultOptions: {
      queries: {
        staleTime: 10_000,
        refetchOnWindowFocus: true,
        refetchIntervalInBackground: false,
        // Without this every 401 was retried three times with backoff before any
        // handler saw it, so the sign-in screen arrived seconds after the session
        // actually lapsed — and a 404 cost the same delay before a panel could say so.
        retry: (failureCount, error): boolean => isRetriableFailure(error) && failureCount < 3,
      },
    },
  });
  return app.client;
}

export const queryClient = createAppQueryClient();
